/* eslint-disable react/no-danger */
import { useMemo } from "react";
import { Box, Button, Divider, Group, Image, Text } from "@mantine/core";
import { IconEdit, IconPencilCheck, IconPlus } from "@tabler/icons-react";
import type {
  ReportSupplementEntry,
  DocumentViewResponse,
} from "../../types/reportTemplate";
import {
  ADDENDUM_RICH_TEXT_CLASS,
  CLINICAL_RICH_TEXT_CLASS,
} from "../../utils/clinicalRichText";
import { resolveRichTextForDisplay, stripHtml } from "../../utils/helpers";
import type { AddendumReportFragment } from "./addendumReportFragments";
import { CLINICAL_REPORT_BODY_STYLE } from "./reportTypography";
import { reportSupplementEntriesOfType } from "./reportSupplementGroups";
import { LAB_TIME_ZONE } from "../../utils/labTime";
import { CptIcdCodesSection } from "./CptIcdCodesSection";
import { AddendumReportPhotoRow } from "./AddendumReportPhotoRow";
import type { OrganMapPhoto } from "./ReportPhotoContent";

type AddendumFragment = Pick<
  AddendumReportFragment,
  "contentHtml" | "showHeading" | "showSignature" | "photoRowIndex"
>;

interface AddendumDocSectionProps {
  addendums: ReportSupplementEntry[];
  fragment?: AddendumFragment;
  cptCodes?: DocumentViewResponse["cptCodes"];
  icdCodes?: DocumentViewResponse["icdCodes"];
  specimenCount?: number;
  showCodes?: boolean;
  reportPhotos?: OrganMapPhoto[];
  photoWidth?: string;
  isReadOnly: boolean;
  onAddAddendum?: () => void;
  onAddAmendment?: () => void;
  onAddCorrection?: () => void;
}

function formatDateTime(dateValue: string): string {
  return new Date(dateValue).toLocaleString("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AddendumDocSection({
  addendums,
  fragment,
  cptCodes = [],
  icdCodes = [],
  specimenCount = 0,
  showCodes = false,
  reportPhotos = [],
  photoWidth = "176pt",
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isReadOnly: _isReadOnly,
  onAddAddendum,
  onAddAmendment,
  onAddCorrection,
}: AddendumDocSectionProps) {
  const trueAddendums = useMemo(
    () => reportSupplementEntriesOfType(addendums, "addendum"),
    [addendums]
  );
  const photoRowsByAddendum = useMemo(
    () =>
      new Map(
        trueAddendums.map((addendum) => {
          const photos = (addendum.reportPhotoIds || []).flatMap((id) =>
            reportPhotos.filter((photo) => photo.image.id === id)
          );
          const rows = fragment
            ? fragment.photoRowIndex === undefined
              ? []
              : [
                  photos.slice(
                    fragment.photoRowIndex * 3,
                    fragment.photoRowIndex * 3 + 3
                  ),
                ]
            : Array.from({ length: Math.ceil(photos.length / 3) }, (_, index) =>
                photos.slice(index * 3, index * 3 + 3)
              );
          return [addendum.id, rows] as const;
        })
      ),
    [trueAddendums, reportPhotos, fragment]
  );

  return (
    <Box mt={fragment?.showHeading === false ? 0 : 16} className="doc-addendum">
      {(fragment ? trueAddendums.slice(0, 1) : trueAddendums).map(
        (addendum) => {
          const content = fragment
            ? fragment.contentHtml
            : resolveRichTextForDisplay(
                addendum.contentHtml || addendum.comments || "",
                {}
              );
          const showHeading = fragment?.showHeading ?? true;
          const showSignature = fragment?.showSignature ?? true;
          const photoRows = photoRowsByAddendum.get(addendum.id) || [];
          return (
            <Box key={addendum.id} mb={showSignature ? 20 : 0}>
              {showHeading && (
                <>
                  <Divider mb={10} />
                  <Text
                    fw={700}
                    size="md"
                    ta="center"
                    style={{ letterSpacing: 2, textDecoration: "underline" }}
                  >
                    ADDENDUM
                  </Text>
                </>
              )}
              {showHeading &&
                (addendum.addendumName || addendum.amendmentReason) && (
                  <Text
                    fw={700}
                    size="md"
                    ta="center"
                    mt={6}
                    style={{ letterSpacing: 2 }}
                  >
                    {addendum.addendumName ||
                      stripHtml(addendum.amendmentReason)}
                  </Text>
                )}
              {content && (
                <Box mt={showHeading ? 8 : 0}>
                  <div
                    className={`${CLINICAL_RICH_TEXT_CLASS} ${ADDENDUM_RICH_TEXT_CLASS}`}
                    style={CLINICAL_REPORT_BODY_STYLE}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </Box>
              )}
              {photoRows.map((row, index) => (
                <AddendumReportPhotoRow
                  key={index}
                  photos={row}
                  width={photoWidth}
                />
              ))}
              {showCodes && showSignature && (
                <Box mt={12}>
                  <CptIcdCodesSection
                    cptCodes={cptCodes}
                    icdCodes={icdCodes}
                    isReadOnly
                    specimenCount={specimenCount}
                  />
                </Box>
              )}
              {showSignature && (
                <Box
                  mt={12}
                  w="100%"
                  data-testid="addendum-signature-footprint"
                >
                  <Box
                    data-testid="addendum-signature-group"
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-start",
                      gap: "6pt",
                    }}
                  >
                    <Text fw={700} size="sm" style={{ letterSpacing: 1 }}>
                      ELECTRONICALLY SIGNED BY:
                    </Text>
                    <Box
                      data-testid="addendum-signature-details"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      {!addendum.isDraft && addendum.signatureImageDataUri && (
                        <Image
                          src={addendum.signatureImageDataUri}
                          alt={`${addendum.signedBy} signature`}
                          fit="contain"
                          style={{
                            height: "33.4pt",
                            width: "auto",
                            maxWidth: "185.5pt",
                            display: "block",
                            marginTop: "2pt",
                          }}
                        />
                      )}
                      <Text size="sm" fw={600} mt={2}>
                        {addendum.signedBy}
                      </Text>
                      {addendum.isDraft ? (
                        <>
                          <Text size="sm">Signature applied at sign-out</Text>
                          <Text size="xs" c="dimmed" fw={700} mt={4}>
                            UNSIGNED ADDENDUM DRAFT
                          </Text>
                        </>
                      ) : (
                        <Text size="sm">
                          {formatDateTime(addendum.signedAt)}
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        }
      )}

      {(onAddAddendum || onAddAmendment || onAddCorrection) && (
        <Box mt={10}>
          <Group gap="xs">
            {onAddAddendum && (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconPlus size={14} />}
                onClick={onAddAddendum}
              >
                Add Addendum
              </Button>
            )}
            {onAddAmendment && (
              <Button
                size="xs"
                variant="subtle"
                color="blue"
                leftSection={<IconEdit size={14} />}
                onClick={onAddAmendment}
              >
                Add Amendment
              </Button>
            )}
            {onAddCorrection && (
              <Button
                size="xs"
                variant="subtle"
                color="orange"
                leftSection={<IconPencilCheck size={14} />}
                onClick={onAddCorrection}
              >
                Add Correction
              </Button>
            )}
          </Group>
        </Box>
      )}
    </Box>
  );
}
